import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { MODULE_CHOICE } from '../../../routes/paths';
import {
  ForgetPasswordStep,
  ForgottenPasswordMethodEnum,
  LoginStep,
  PasswordRulesType,
  SignInReducerActions,
  SignInReducerActionsType,
  UseDataProps,
} from './types';
import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';
import { useStore } from '30.quickConnect.Stores';
import { ForgottenPasswordRequest, SignInRequest } from '30.quickConnect.Stores/RootStore/LoginStore/Payloads/requests';
import {
  AppModulesIDs,
  DispatcherResponse,
  ForgottenPasswordResponse,
  Modules,
} from '30.quickConnect.Stores/RootStore/LoginStore/Payloads/responses';
import { verifyResetCodeAndPassword } from '80.quickConnect.Core/helpers/verifyForm';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { IdentityProviders, ResponseStatus } from '90.quickConnect.Models/enums';
import { LoginAttemptResult } from '30.quickConnect.Stores/RootStore/LoginStore/types';

const DEFAULT_MODULE = (t: TFunction) => {
  return {
    [t('qcapp_web_client')]: AppModulesIDs.MODULE_WEB_CLIENT_ID,
  };
};

const DEFAULT_SIGN_IN_REQUEST = {
  userUPN: '',
  password: '',
  cgu: false,
  module: AppModulesIDs.EMPTY_ID,
} as SignInRequest;

/*
 * Un code n'est envoyé que pour l'email ou le sms (statut Pending).
 * Pour une demande d'aide à l'administrateur, il n'y a rien à saisir.
 */
const isResetCodeSent = (response: ForgottenPasswordResponse): boolean => response.status === ResponseStatus.Pending;

const reducer = (state: SignInRequest, action: SignInReducerActions) => {
  switch (action.type) {
    case SignInReducerActionsType.USERUPN:
      return {
        ...state,
        userUPN: action.value,

        // Un mot de passe ne doit jamais être conservé
        // lorsqu'on change d'identifiant.
        password: '',
      };

    case SignInReducerActionsType.PASSWORD:
      return { ...state, password: action.value };
    case SignInReducerActionsType.CGU:
      return { ...state, cgu: action.value };
    case SignInReducerActionsType.MODULE:
      return { ...state, module: action.value };
    case SignInReducerActionsType.RESET:
      return DEFAULT_SIGN_IN_REQUEST;
    default:
      return state;
  }
};

const useLoginData = (t: TFunction): UseDataProps => {
  // Tag
  const tag = '10.quickConnect.app/components/domain/Login/hooks.ts';
  // On récupère le store
  const {
    LoginStore: {
      logInAsync,
      isLogging,
      setIsLogging,
      authenticationMethod,
      isUserInactive,
      verifyFieldUpnAndCgu,
      connectByMsal,
      dispatcherAsync,
      setUrlToUse,
      getAvailableMethods,
      getResetCode,
      resetCodeLoader,
      sendResetCode,
      availableModules,
      signInInfos: { userUPN, module, emailSupport },
    },
    LoginMsalStore: { handleRedirectPromise },
  } = useStore();

  const navigate = useNavigate();

  // Add defaultModule (Web Client)
  const availableModuleAndWebClient = useCallback((): Modules => {
    if (!availableModules) {
      return DEFAULT_MODULE(t);
    }
    return { ...DEFAULT_MODULE(t), ...availableModules };
  }, [availableModules, t]);

  // On set le state
  const { t: tAxios } = useTranslation('axios');

  const [passwordRules, setPasswordRules] = useState<PasswordRulesType>({
    passLength: false,
    lowerCase: false,
    upperCase: false,
    digit: false,
    specialCharacters: false,
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [forgetPasswordStep, setForgetPasswordStep] = useState<ForgetPasswordStep>(ForgetPasswordStep.None);
  const [availableMethods, setAvailableMethods] = useState<ForgottenPasswordResponse>();
  const [method, setMethod] = useState<ForgottenPasswordMethodEnum>();
  const [resetPasswordData, setResetPasswordData] = useState<ForgottenPasswordResponse>();

  const [signInRequest, setSignInRequest] = useReducer(reducer, {
    ...DEFAULT_SIGN_IN_REQUEST,
    userUPN,
  });

  /*
   * L'étape du formulaire est indépendante du mode
   * d'authentification conservé dans LoginStore.
   *
   * Identification  : seul l'identifiant est considéré comme validable.
   * Authentication : l'identifiant courant a été reconnu par l'API
   *               comme utilisateur QuickConnect nécessitant un mot de passe.
   */
  const [loginStep, setLoginStep] = useState<LoginStep>(LoginStep.Identification);

  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [resetCode, setResetCode] = useState<string>('');

  /*
   * Toute modification de l'identifiant invalide immédiatement
   * l'étape d'authentification précédemment déterminée.
   *
   * Le reducer vide également le password associé
   * à l'ancien identifiant.
   */
  const onUserUPNChange = useCallback((value: string): void => {
    setLoginStep(LoginStep.Identification);

    // Si le mot de passe était affiché en clair, on revient
    // également à son état sécurisé.
    setShowPassword(false);

    setSignInRequest({
      type: SignInReducerActionsType.USERUPN,
      value,
    });
  }, []);

  const onForgetPassword = useCallback(async () => {
    try {
      const data = await getAvailableMethods(signInRequest.userUPN);
      if (data) {
        setAvailableMethods(data);
        // Une seule méthode était disponible : l'api a déjà envoyé le code, il n'y a pas de choix à faire
        if (isResetCodeSent(data)) {
          setResetPasswordData(data);
          setForgetPasswordStep(ForgetPasswordStep.ResetPassword);
        } else {
          setForgetPasswordStep(ForgetPasswordStep.SelectMethod);
        }
      }
    } catch (error: unknown) {
      errorHandler(tag, error, 'onForgetPassword');
    }
  }, [getAvailableMethods, signInRequest.userUPN, tag]);

  /*
   * L'affichage du mot de passe dépend maintenant explicitement
   * de l'étape courante du formulaire.
   *
   * authenticationMethod seul ne suffit pas car sa valeur peut
   * correspondre à l'identifiant précédemment validé.
   */
  const showPasswordField =
    loginStep === LoginStep.Authentication &&
    authenticationMethod === IdentityProviders.QuickConnect &&
    !isUserInactive;

  const onGoBackToLogin = useCallback(() => {
    // Réinitialiser les champs
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setMethod(undefined);
    setAvailableMethods(undefined);
    setResetPasswordData(undefined);
    setForgetPasswordStep(ForgetPasswordStep.None);

    // Retour explicite à l'étape initiale.
    setLoginStep(LoginStep.Identification);

    setShowPassword(false);

    setSignInRequest({
      type: SignInReducerActionsType.RESET,
    });
  }, []);

  const onResetPassword = useCallback(async () => {
    if (verifyResetCodeAndPassword(false, resetCode, newPassword, confirmPassword, passwordRules, t)) {
      const info: ForgottenPasswordRequest = {
        userUpn: signInRequest.userUPN,
        method,
        newPassword,
        resetCode,
      };
      try {
        const data = await sendResetCode(info, t);
        if (data) {
          setResetCode('');
          setNewPassword('');
          setConfirmPassword('');
          setMethod(undefined);
          setForgetPasswordStep(ForgetPasswordStep.None);
          setLoginStep(LoginStep.Identification);
        }
      } catch (error: unknown) {
        errorHandler(tag, error, 'onResetPassword');
      }
    }
  }, [confirmPassword, method, tag, newPassword, passwordRules, resetCode, sendResetCode, signInRequest.userUPN, t]);

  const onSelectMethod = useCallback(async () => {
    try {
      const info: ForgottenPasswordRequest = {
        userUpn: signInRequest.userUPN,
        method,
      };
      const data = await getResetCode(info, t);
      if (data) {
        setResetPasswordData(data);
        setForgetPasswordStep(
          isResetCodeSent(data) ? ForgetPasswordStep.ResetPassword : ForgetPasswordStep.RequestSent,
        );
      }
    } catch (error: unknown) {
      errorHandler(tag, error, 'onSelectPassword');
    }
  }, [signInRequest.userUPN, method, getResetCode, t, tag]);

  /**
   * Permet de garder le BackDrop open dans le cas d'une connexion par MSAL
   */
  const isOpenBackdrop = useMemo((): boolean => isLogging || connectByMsal, [isLogging, connectByMsal]);

  /*
   * Centralise la transition entre :
   *
   * Identifier -> Credentials
   *
   * La transition n'est faite que si l'API indique explicitement
   * qu'un mot de passe QuickConnect est nécessaire.
   *
   * Une erreur de mot de passe ne remet volontairement pas
   * LoginStep à Identifier afin de permettre à l'utilisateur
   * de corriger son mot de passe.
   */
  const logInAndUpdateStepAsync = useCallback(
    async (request: SignInRequest): Promise<void> => {
      const result = await logInAsync(request, tAxios);

      if (result === LoginAttemptResult.PasswordRequired) {
        setLoginStep(LoginStep.Authentication);
      }
    },
    [logInAsync, tAxios],
  );

  const connect = useCallback(async (): Promise<void> => {
    if (!verifyFieldUpnAndCgu(signInRequest, tAxios)) {
      return;
    }

    try {
      const env = process.env.REACT_APP_ENV;
      const shouldBypassDispatcher = env === 'dev' || env === 'demo' || env === 'int';
      if (shouldBypassDispatcher) {
        await logInAndUpdateStepAsync(signInRequest);
        return;
      }

      const result: DispatcherResponse | undefined = await dispatcherAsync({
        userUpn: signInRequest.userUPN,
      });

      if (!result) {
        return;
      }

      if (result.availableEnvironment.length > 1) {
        const customerNames = result.availableEnvironment.map((item) => item.customerName);

        toast.error(
          tAxios('qcapp_warning_error_config_dispatcher_multi_env', {
            customerName: customerNames,
          }).toString(),
        );

        setIsLogging(false);

        return;
      }

      if (
        result.availableEnvironment.length === 1 &&
        process.env.REACT_APP_QC_API_ENDPOINT?.search(result.availableEnvironment[0].urlQuickConnect) === -1
      ) {
        setUrlToUse(`${result.availableEnvironment[0].urlQuickConnect}/api/`);
      }

      /*
       * Un seul point d'appel au login après traitement
       * éventuel du dispatcher.
       */
      await logInAndUpdateStepAsync(signInRequest);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<any>;
        if (errorAxios?.response?.status === 404) {
          toast.error(tAxios('logInAsync_userNotFound').toString());
          setIsLogging(false);
        }
      }
    }
  }, [
    verifyFieldUpnAndCgu,
    signInRequest,
    tAxios,
    dispatcherAsync,
    setIsLogging,
    setUrlToUse,
    logInAndUpdateStepAsync,
  ]);

  useEffectOnce(() => {
    if (connectByMsal) {
      (async () => {
        const canConnect = await handleRedirectPromise();
        if (canConnect && (module === AppModulesIDs.EMPTY_ID || module === AppModulesIDs.MODULE_WEB_CLIENT_ID)) {
          navigate(MODULE_CHOICE);
        }
      })();
    }
  });

  // on init we get the value
  useEffect(() => {
    function isEmpty(obj: any) {
      for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
          return false;
        }
      }
      return true;
    }

    if (
      !isLogging &&
      signInRequest.module === AppModulesIDs.EMPTY_ID &&
      availableModules &&
      !isEmpty(availableModules)
    ) {
      const defaultSelectedModule = AppModulesIDs.MODULE_WEB_CLIENT_ID;
      setSignInRequest({ type: SignInReducerActionsType.MODULE, value: defaultSelectedModule });
    }
  }, [availableModuleAndWebClient, availableModules, isLogging, signInRequest.module]);

  return {
    signInRequest,
    setSignInRequest,
    onUserUPNChange,
    connect,
    showPassword,
    setShowPassword,
    isOpenBackdrop,
    isLogging,
    showPasswordField,
    forgetPasswordStep,
    onForgetPassword,
    availableMethods,
    setMethod,
    method,
    onSelectMethod,
    onGoBackToLogin,
    confirmPassword,
    newPassword,
    setConfirmPassword,
    setNewPassword,
    resetCode,
    setResetCode,
    passwordRules,
    setPasswordRules,
    resetPasswordData,
    onResetPassword,
    resetCodeLoader,
    availableModules: availableModuleAndWebClient(),
    currentModule: signInRequest.module,
    emailSupport,
  };
};

export default useLoginData;
